import Vue from 'vue';
import { buildConfigMap } from '@/helpers/filters';

function resolveConfigMap() {
  const userConfig = Vue.prototype.$auth?.userConfig;
  return buildConfigMap(userConfig || []);
}

/**
 * Checks if MAV feature is enabled for viewing (v1 or v2)
 * @param {string} configKey - Optional config key (defaults to 'mavFeature_owl_ui')
 * @returns {boolean}
 */
export const canViewMav = (configKey = 'mavFeature_owl_ui') => {
  const config = resolveConfigMap();
  const mavConfig = config[configKey];
  return mavConfig?.enabled?.v1 || mavConfig?.enabled?.v2 || false;
};

/**
 * Checks if MAV v2 is enabled
 * @param {string} configKey - Optional config key (defaults to 'mavFeature_owl_ui')
 * @returns {boolean}
 */
export const isV2Enabled = (configKey = 'mavFeature_owl_ui') => {
  const config = resolveConfigMap();
  return config[configKey]?.enabled?.v2 || false;
};

/**
 * Checks if MAV v1 is enabled (for tree ID predictions)
 * @param {string} configKey - Optional config key (defaults to 'mavFeature_owl_ui')
 * @returns {boolean}
 */
export const isV1Enabled = (configKey = 'mavFeature_owl_ui') => {
  const config = resolveConfigMap();
  return config[configKey]?.enabled?.v1 || false;
};

/**
 * Checks if debug mode is enabled for MAV features
 * @param {string} configKey - Optional config key (defaults to 'mavFeature_owl_ui')
 * @returns {boolean}
 */
export const isDebugEnabled = (configKey = 'mavFeature_owl_ui') => {
  const config = resolveConfigMap();
  return config[configKey]?.enabled?.debug === true;
};

/**
 * Checks if shadow mode is enabled (shadow only, no v1/v2)
 * @param {string} configKey - Optional config key (defaults to 'mavFeature_owl_ui')
 * @returns {boolean}
 */
export const isShadowMode = (configKey = 'mavFeature_owl_ui') => {
  const config = resolveConfigMap();
  const mavConfig = config[configKey];
  return mavConfig?.enabled?.shadow === true && !mavConfig?.enabled?.v1 && !mavConfig?.enabled?.v2;
};

/**
 * Checks if production mode is enabled (v1 or v2, not shadow)
 * @param {string} configKey - Optional config key (defaults to 'mavFeature_owl_ui')
 * @returns {boolean}
 */
export const isProductionMode = (configKey = 'mavFeature_owl_ui') => {
  const config = resolveConfigMap();
  const mavConfig = config[configKey];
  return (mavConfig?.enabled?.v1 || mavConfig?.enabled?.v2) && !mavConfig?.enabled?.shadow;
};

/**
 * Checks if subcategory feature is enabled
 * @returns {boolean}
 */
export const canViewSubcategory = () => {
  const config = resolveConfigMap();
  const subcatConfig = config['mavFeature_owl_ui']?.subcat_enabled;
  return subcatConfig?.v1 || subcatConfig?.v2 || false;
};

/**
 * Checks if subcategory shadow mode is enabled
 * @returns {boolean}
 */
export const isSubcategoryShadowMode = () => {
  const config = resolveConfigMap();
  const subcatConfig = config['mavFeature_owl_ui']?.subcat_enabled;
  return subcatConfig?.shadow === true && !subcatConfig?.v1 && !subcatConfig?.v2;
};

/**
 * Checks if subcategory production mode is enabled
 * @returns {boolean}
 */
export const isSubcategoryProductionMode = () => {
  const config = resolveConfigMap();
  const subcatConfig = config['mavFeature_owl_ui']?.subcat_enabled;
  return (subcatConfig?.v1 || subcatConfig?.v2) && !subcatConfig?.shadow;
};

/**
 * Checks if app feature is enabled (v1)
 * @returns {boolean}
 */
export const canViewApp = () => {
  const config = resolveConfigMap();
  return config['mavFeature_owl_ui']?.app_enabled?.v1 || false;
};

/**
 * Checks if app shadow mode is enabled (shadow only, no v1)
 * @returns {boolean}
 */
export const isAppShadowMode = () => {
  const config = resolveConfigMap();
  const appConfig = config['mavFeature_owl_ui']?.app_enabled;
  return appConfig?.shadow === true && !appConfig?.v1;
};

/**
 * Checks if app production mode is enabled (v1, not shadow)
 * @returns {boolean}
 */
export const isAppProductionMode = () => {
  const config = resolveConfigMap();
  const appConfig = config['mavFeature_owl_ui']?.app_enabled;
  return appConfig?.v1 === true && !appConfig?.shadow;
};

/**
 * Checks if shadow metrics should be sent to Google Chat webhooks.
 * @returns {boolean}
 */
export const isSendShadowMetricsEnabled = () => {
  const config = resolveConfigMap();
  return config['sendShadowMetrics_owl_ui']?.enabled === true;
};
